import { create } from "zustand";
import i18n from "i18next";

export type ForkliftStatus = "loaded" | "empty" | "idle" | "offline" | "linkdown";

export interface ForkliftPose {
  id: string;
  series: string;
  x: number;
  y: number;
  yaw: number;
  speed: number;
  status: ForkliftStatus;
  battery: number;       // 0..1
  loaded: boolean;
  lastSeenMs: number;
  modelUrl?: string;
  modelScale?: number;
}

export interface GapMarker {
  assetId: string;
  fromMs: number;
  toMs: number;
  receivedMs: number;
}

/** Forklifts not seen within this window are shown as offline. */
export const STALE_MS = 10_000;

/**
 * Returns the display status for a forklift, treating any entry not seen
 * within STALE_MS as offline. Use this in every screen instead of fork.status
 * directly so staleness is consistent everywhere.
 */
export function getDisplayStatus(fork: ForkliftPose): ForkliftStatus {
  if (!fork.lastSeenMs || Date.now() - fork.lastSeenMs > STALE_MS) return "offline";
  return fork.status;
}

/** Locale-aware relative-time label (e.g. "há 5 min" / "5m ago"). */
export function relativeTime(ms: number): string {
  if (!ms) return i18n.t("time.never");
  const diff = Math.floor((Date.now() - ms) / 1000);
  if (diff < 10)   return i18n.t("time.now");
  if (diff < 60)   return i18n.t("time.secondsAgo", { n: diff });
  if (diff < 3600) return i18n.t("time.minutesAgo", { n: Math.floor(diff / 60) });
  return i18n.t("time.hoursAgo", { n: Math.floor(diff / 3600) });
}

/** A gateway heartbeat (10 s cadence) older than this is treated as offline. */
export const GATEWAY_STALE_MS = 30_000;

/**
 * True when `assetId` has a bound gateway that reports online and whose last
 * heartbeat is fresh. Lets screens show a commissioned-but-poseless asset
 * (edge up, camera not localized) instead of hiding it as plain offline.
 */
export function gatewayOnlineForAsset(
  gateways: Record<string, GatewayStatus>,
  assetId: string,
  nowMs: number = Date.now(),
): boolean {
  return Object.values(gateways).some(
    (g) => g.assetId === assetId && g.online && nowMs - g.lastSeenMs < GATEWAY_STALE_MS,
  );
}

/**
 * Whether an asset belongs on the floor of a site (ADR 394). On an RTLS site every
 * registered asset does. On a site with RTLS switched off the roster is not a fleet to
 * show — only an asset whose gateway is online right now is (an iC3 kit mid-count).
 */
export function assetShownOnSite(
  hasRtls: boolean,
  gateways: Record<string, GatewayStatus>,
  assetId: string,
): boolean {
  return hasRtls || gatewayOnlineForAsset(gateways, assetId);
}

export interface AlertRecord {
  id: string;
  siteId: string;
  /** Null on inventory-derived alerts (ADR 282) — their scope rides `detail`. */
  assetId: string | null;
  code: string;
  severity: 'INFO' | 'WARN' | 'CRIT';
  ts: number;
  status: 'open' | 'ack' | 'resolved';
  detail?: Record<string, unknown>;
}

export interface SensorReading {
  id: string;
  health: 'ok' | 'warn' | 'fail' | 'offline';
  value?: number;
  unit?: string;
  ts: number;
}

export type PeripheralStatus = {
  ok: boolean;
  lastPoseAgeMs?: number;
  type?: string;
};

/** ADR 365: the iC3 kit adapter's status block, when the gateway runs one. */
export type Ic3Status = {
  kitWsOk: boolean;
  localized: boolean;
  runPhase: "idle" | "inventorying" | "paused" | string;
  outboxPending: number;
  outboxOldestAgeS: number | null;
  evidencePending: number;
  lastUploadOkTs: number | null;
  lastUploadError: string | null;
};

export type GatewayStatus = {
  id: string;
  assetId?: string;
  online: boolean;
  lastSeenMs: number;
  peripherals?: {
    v2pro?: PeripheralStatus;
    forkSensor?: PeripheralStatus;
    bufferFill?: number;
  };
  ic3?: Ic3Status;
};

interface TelemetryState {
  forklifts: Record<string, ForkliftPose>;
  gaps: GapMarker[];
  /** Asset whose detail panel is open. Setting this is cheap; it does NOT
   *  pin the camera to the asset. Continuous camera tracking is controlled
   *  separately by `followAssetId`. Previously selection auto-activated the
   *  follow-cam, which made the orbit snap back on every mouseup. */
  selectedAssetId: string | null;
  /** Asset the camera is continuously following. Set via setFollow() (e.g.
   *  double-click on a robot or an explicit "Seguir" action). Cleared by
   *  clearSelection() or setFollow(null). */
  followAssetId: string | null;
  /** Monotonic counter; bumped whenever a one-shot camera focus is requested.
   *  Scene.tsx watches this and performs a brief lerp toward the selected
   *  asset, then stops. Avoids the perpetual `useFrame` lerp that used to
   *  override user-driven orbit. */
  focusRequestId: number;
  alerts: Record<string, AlertRecord>;
  sensorReadings: Record<string, SensorReading>;
  gateways: Record<string, GatewayStatus>;
  /** ADR 324 — Mode I1: the addresses an in-progress iC3 run has REACHED, and when.
   *  Coverage as it happens, never stock: the wire carries no product and no quantity,
   *  because the kit's mid-run association is mode counting and the next read can
   *  overturn it. `null` until a run streams something. */
  inventoryActivity: InventoryActivity | null;

  applyDelta: (p: Partial<ForkliftPose> & { id: string }) => void;
  applyGap: (g: Omit<GapMarker, "receivedMs">) => void;
  setRoster: (roster: Array<{ id: string; series?: string; typeCode?: string; modelUrl?: string; typeModelUrl?: string; typeModelScale?: number }>) => void;
  /** Open the inspector for `assetId` and request a one-shot camera focus.
   *  Passing null only clears the selection (no focus). */
  select: (assetId: string | null) => void;
  /** Start (or stop) continuous camera follow. */
  setFollow: (assetId: string | null) => void;
  /** Clear both selection and follow — used for click-on-empty-space deselect. */
  clearSelection: () => void;
  reset: () => void;
  applyAlertDelta: (a: AlertRecord) => void;
  clearAlert: (id: string) => void;
  applySensorDelta: (r: SensorReading) => void;
  applyGatewayStatus: (status: GatewayStatus) => void;
  applyInventoryActivity: (a: InventoryActivityDelta) => void;
}

/** One second of an in-progress run's visits, as the realtime service fans it out. */
export interface InventoryActivityDelta {
  siteId: string;
  warehouseId: string | null;
  runId: string | null;
  addresses: string[];
  tsMs: number;
  poseX?: number | null;
  poseY?: number | null;
  /** ADR 363: when the realtime service first saw this sweep (epoch ms), if it knows. */
  firstTsMs?: number | null;
  /** ADR 363: true for the on-subscribe replay of everything so far — it must not
   *  count as "this second" when the pace is estimated. */
  snapshot?: boolean;
}

export interface InventoryActivity {
  siteId: string;
  warehouseId: string | null;
  runId: string | null;
  /** address → when it was last reached, epoch ms. Accumulates for the run: the
   *  question is "how far has this sweep got", which is the union, not the last second. */
  visited: Map<string, number>;
  lastTsMs: number;
  poseX: number | null;
  poseY: number | null;
  /** ADR 363: sweep start as the realtime service saw it; null until a delta says. */
  firstTsMs: number | null;
  /** ADR 363: wall-clock ms when this tab started receiving LIVE (non-snapshot) deltas —
   *  the pace window never reaches before it, or a snapshot would read as a burst. */
  liveSinceMs: number | null;
  /** ADR 363: first-visit timestamps of addresses that arrived in live deltas, in
   *  arrival order. Snapshot addresses are not in here (their real first visit is unknown). */
  visitLog: number[];
}

function inferStatus(p: Partial<ForkliftPose>): ForkliftStatus {
  // "offline"/"linkdown" are connectivity presentation states (roster seed,
  // staleness) — a delta that just arrived always re-infers a live status,
  // otherwise a roster-seeded "offline" survives every delta forever.
  if (p.status && p.status !== "idle" && p.status !== "offline" && p.status !== "linkdown") return p.status;
  if (p.loaded) return "loaded";
  if ((p.speed ?? 0) > 0.05) return "empty";
  return "idle";
}

export const useTelemetryStore = create<TelemetryState>((set) => ({
  forklifts: {},
  gaps: [],
  selectedAssetId: null,
  followAssetId:   null,
  focusRequestId:  0,
  alerts: {},
  sensorReadings: {},
  gateways: {},
  inventoryActivity: null,

  applyDelta(p) {
    set((s) => {
      const existing = s.forklifts[p.id];
      const base: ForkliftPose = {
        id: p.id,
        series: "F",
        x: 0, y: 0, yaw: 0, speed: 0,
        battery: 0,   // 0 until real data arrives — never show a fake value
        loaded: false,
        lastSeenMs: Date.now(),
        status: "idle",
      };
      const merged: ForkliftPose = { ...base, ...existing, ...p };
      merged.status = inferStatus(merged);
      return { forklifts: { ...s.forklifts, [p.id]: merged } };
    });
  },

  setRoster(roster) {
    set((s) => {
      // Seed entries for forklifts not yet in the store so screens see the full fleet.
      // Existing live-data entries are NOT overwritten.
      const seeds: Record<string, ForkliftPose> = {};
      for (const f of roster) {
        if (!s.forklifts[f.id]) {
          // typeCode is the canonical identifier; series is the legacy WS field name.
          const resolvedSeries = f.typeCode ?? f.series ?? "F";
          // Per-unit modelUrl takes precedence over the type's defaultModelUrl.
          const resolvedUrl = (f.modelUrl || undefined) ?? (f.typeModelUrl || undefined);
          seeds[f.id] = {
            id: f.id,
            series: resolvedSeries,
            x: 0, y: 0, yaw: 0, speed: 0,
            battery: 0,
            loaded: false,
            status: "offline",
            lastSeenMs: 0,
            modelUrl: resolvedUrl,
            modelScale: f.typeModelScale ?? 1,
          };
        }
      }
      return { forklifts: { ...seeds, ...s.forklifts } };
    });
  },

  applyGap(g) {
    set((s) => ({ gaps: [...s.gaps.slice(-99), { ...g, receivedMs: Date.now() }] }));
  },
  select(assetId) {
    set((s) => assetId === null
      ? { selectedAssetId: null }
      : { selectedAssetId: assetId, focusRequestId: s.focusRequestId + 1 });
  },
  setFollow(assetId) {
    set((s) => ({
      followAssetId:   assetId,
      // Open the panel for the followed asset too so the user sees its data.
      selectedAssetId: assetId ?? s.selectedAssetId,
    }));
  },
  clearSelection() {
    set({ selectedAssetId: null, followAssetId: null });
  },
  reset() {
    set({
      forklifts: {}, gaps: [],
      selectedAssetId: null, followAssetId: null, focusRequestId: 0,
      alerts: {}, sensorReadings: {}, gateways: {},
      inventoryActivity: null,
    });
  },
  applyAlertDelta(a) {
    set((s) => ({ alerts: { ...s.alerts, [a.id]: a } }));
  },
  clearAlert(id) {
    set((s) => {
      const next = { ...s.alerts };
      delete next[id];
      return { alerts: next };
    });
  },
  applySensorDelta(r) {
    set((s) => ({ sensorReadings: { ...s.sensorReadings, [r.id]: r } }));
  },
  applyGatewayStatus(status) {
    set((s) => ({ gateways: { ...s.gateways, [status.id]: status } }));
  },
  applyInventoryActivity(a) {
    set((s) => {
      const prev = s.inventoryActivity;
      // A different run is a different sweep, so its coverage starts empty. Carrying the
      // previous run's visited set forward would show a fresh run as already half done.
      const fresh = !prev || prev.runId !== a.runId || prev.warehouseId !== a.warehouseId;
      const visited = fresh ? new Map<string, number>() : new Map(prev.visited);
      const visitLog = fresh ? [] : prev.visitLog.slice();
      let liveSinceMs = fresh ? null : prev.liveSinceMs;
      if (!a.snapshot && liveSinceMs === null) liveSinceMs = Date.now();
      for (const address of a.addresses) {
        // ADR 363: only a genuinely new address in a LIVE delta is a first visit the
        // pace can be read from; a snapshot replays history with one timestamp.
        if (!a.snapshot && !visited.has(address)) visitLog.push(a.tsMs);
        visited.set(address, a.tsMs);
      }
      return {
        inventoryActivity: {
          siteId: a.siteId,
          warehouseId: a.warehouseId,
          runId: a.runId,
          visited,
          lastTsMs: Math.max(a.tsMs, fresh ? 0 : prev.lastTsMs),
          poseX: a.poseX ?? null,
          poseY: a.poseY ?? null,
          firstTsMs: a.firstTsMs ?? (fresh ? null : prev.firstTsMs),
          liveSinceMs,
          visitLog,
        },
      };
    });
  },
}));
